import { useRef } from "react";
import { Plus, X, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";

/**
 * ImageRefSlots — 5 generic, unlabeled reference-image slots for AI Image mode.
 * Any combination of slots can hold a reference image in any order. Empty
 * slots show a soft "+" affordance and accept both file-picker uploads and
 * drag-drops (from disk or from the generation feed).
 *
 * Slots are backed by the same `omniRefs` array used elsewhere — each slot
 * either holds a ref object `{url, uploaded_url, file}` or `null`.
 *
 * Props:
 *   refs: array of ref objects (0..5)
 *   onPick(file): user picked a local file for the next empty slot
 *   onRemove(index): remove the slot at index (compacts the array)
 *   onDropFeed?(item, index): a feed item was dropped onto slot `index`
 *   max: cap (default 5)
 */
export function ImageRefSlots({
  refs = [],
  onPick,
  onRemove,
  onDropFeed,
  max = 5,
  testId = "image-ref-slots",
}) {
  const inputRef = useRef(null);
  const slots = Array.from({ length: max }, (_, i) => refs[i] || null);
  const activeCount = refs.filter(Boolean).length;

  const handleFilesPicked = (files) => {
    if (!files || !files.length) return;
    const remaining = max - activeCount;
    if (remaining <= 0) { toast.error(`Attachment limit is ${max}.`); return; }
    Array.from(files).slice(0, remaining).forEach((f) => onPick?.(f));
  };

  const handleDrop = (e, idx) => {
    e.preventDefault();
    e.stopPropagation();
    // Feed-item drag payload (JSON) takes precedence over local files
    const feedPayload = e.dataTransfer.getData("application/x-geneo-feed-item");
    if (feedPayload) {
      try {
        const item = JSON.parse(feedPayload);
        onDropFeed?.(item, idx);
      } catch { /* ignore */ }
      return;
    }
    if (e.dataTransfer.files && e.dataTransfer.files.length) {
      handleFilesPicked(e.dataTransfer.files);
    }
  };

  return (
    <div className="space-y-2" data-testid={testId}>
      <div className="flex items-center justify-between">
        <span className="font-mono-hy text-[9px] text-white tracking-[0.22em]">
          REFERENCES · {activeCount}/{max}
        </span>
        {activeCount > 0 && (
          <span className="font-mono-hy text-[8px] text-white tracking-[0.18em]">
            DROP OR TAP TO ADD
          </span>
        )}
      </div>

      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        multiple
        onChange={(e) => { handleFilesPicked(e.target.files); e.target.value = ""; }}
        className="hidden"
        data-testid={`${testId}-file-input`}
      />

      <div className="grid grid-cols-5 gap-2">
        {slots.map((ref, i) => {
          if (ref) {
            return (
              <div
                key={i}
                onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = "copy"; }}
                onDrop={(e) => handleDrop(e, i)}
                data-testid={`${testId}-slot-${i}`}
                className="relative aspect-square rounded-xl overflow-hidden border border-[#00E5FF]/40 bg-[#0A0A0A] shadow-[0_0_14px_-6px_rgba(0,229,255,0.6)] group"
              >
                <img src={ref.url} alt="" className="absolute inset-0 w-full h-full object-cover" />
                <button
                  type="button"
                  onClick={() => onRemove?.(i)}
                  data-testid={`${testId}-remove-${i}`}
                  className="absolute top-1 right-1 w-5 h-5 rounded-full bg-[#0A0A0A]/80 border border-[#EB5757]/60 text-[#EB5757] hover:bg-[#EB5757] hover:text-white flex items-center justify-center transition-all backdrop-blur"
                  aria-label={`Remove reference ${i + 1}`}
                >
                  <X className="w-2.5 h-2.5" />
                </button>
                {!ref.uploaded_url && (
                  <div className="absolute inset-x-0 bottom-0 h-1 bg-[#F2C94C]/60 animate-pulse" title="Uploading…" />
                )}
              </div>
            );
          }
          // Empty slot — the FIRST empty slot is the "next pick" target
          const isNextEmpty = slots.findIndex((s) => !s) === i;
          return (
            <button
              key={i}
              type="button"
              onClick={() => inputRef.current?.click()}
              onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = "copy"; }}
              onDrop={(e) => handleDrop(e, i)}
              data-testid={`${testId}-slot-${i}-empty`}
              className={`relative aspect-square rounded-xl border-2 border-dashed flex flex-col items-center justify-center gap-1 transition-all group ${
                isNextEmpty
                  ? "border-[#00E5FF]/40 bg-[#00E5FF]/[0.03] hover:border-[#00E5FF] hover:bg-[#00E5FF]/[0.06] hover:shadow-[0_0_16px_-6px_rgba(0,229,255,0.5)]"
                  : "border-white/[0.08] bg-white/[0.015] hover:border-[#00E5FF]/40"
              }`}
              aria-label={`Empty reference slot ${i + 1}`}
            >
              <Plus className={`w-4 h-4 transition-transform group-hover:scale-110 ${isNextEmpty ? "text-[#00E5FF]" : "text-white"}`} />
              <span className={`font-mono-hy text-[8px] tracking-widest ${isNextEmpty ? "text-[#00E5FF]/80" : "text-white"}`}>
                SLOT {i + 1}
              </span>
            </button>
          );
        })}
      </div>

      <p className="font-mono-hy text-[9px] text-white leading-relaxed">
        Drop images or drag any completed render from the feed. Slots blend equally into every generation.
      </p>
    </div>
  );
}

export default ImageRefSlots;
